import { Button } from '@breetometer/components';
import { ImageComponent } from 'BreetComponents';
import { showToast } from 'BreetHelpers';
import QrScanner from 'qr-scanner';
import { useCallback, useEffect, useRef } from 'react';

import qrframe from '../../assets/images/qr-frame.svg';
import type { IQrReader } from './QrReader.helpers';

const QrReader = ({ setAddress, onClose }: IQrReader) => {
	const scanner = useRef<QrScanner | null>(null);
	const videoEl = useRef<HTMLVideoElement>(null);
	const qrBoxEl = useRef<HTMLDivElement>(null);
	const lastScanRef = useRef<string>('');

	const handleClose = useCallback(() => {
		scanner.current?.destroy();
		onClose?.();
	}, [onClose]);

	const onScanSuccess = useCallback(
		(result: QrScanner.ScanResult) => {
			const data = result.data.trim();

			if (data === lastScanRef.current) {
				return;
			}

			lastScanRef.current = data;

			const isValid = data.length > 0 && data.length <= 100 && /^[a-zA-Z0-9\-_.]+$/.test(data);

			if (!isValid) {
				showToast('error', 'Invalid QR code data. Please scan a valid code.');

				setTimeout(() => {
					lastScanRef.current = '';
				}, 2000);

				return;
			}

			setAddress(data);

			handleClose();
		},
		[setAddress, handleClose]
	);

	useEffect(() => {
		const videoElCurrent = videoEl.current;

		if (!videoElCurrent) return;

		const scannerInstance = new QrScanner(videoElCurrent, onScanSuccess, {
			preferredCamera: 'environment',
			highlightCodeOutline: true,
			overlay: qrBoxEl.current ?? undefined,
		});

		scannerInstance.start().catch((err: unknown) => {
			if (err) {
				showToast('error', 'Camera is blocked or not accessible. Please allow camera in your browser permissions and Reload.');
			}
		});

		scanner.current = scannerInstance;

		return () => {
			scannerInstance.destroy();
			scanner.current = null;
			lastScanRef.current = '';
		};
	}, [onScanSuccess]);

	return (
		<div>
			<div className='qr-reader border-dark-navy bg-primary-600'>
				<video
					ref={videoEl}
					autoPlay
					playsInline
				>
					<track
						kind='captions'
						src='/captions.vtt'
						srcLang='en'
						label='English'
						default
					/>
				</video>

				<div className='qr-reader_box'>
					<ImageComponent
						src={qrframe}
						alt='Qr Frame'
						width={256}
						height={256}
						className='qr-reader_frame'
					/>
				</div>
			</div>

			<Button
				variant='tertiary'
				onClick={handleClose}
				label='Close'
				className='qr-reader_close theme-background-none border-dark-navy color-primary-100'
				type='button'
			/>
		</div>
	);
};

export default QrReader;
